import { SettingsIcon, UserIcon } from '@assets/icons';
import { Group } from '@components/layout';
import { OptionsMenu } from '@components/menu';
import { useManageServerMembersModal } from '@components/server';
import { Typography } from '@components/typography';
import { type Editor } from '@generated/graphql';
import { Divider, MenuItem, type SxProps } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { CopySshLaunchCommandMenuItem } from './CopySshLaunchCommandMenuItem';
import { DeleteServerOption } from './DeleteServerOption';
import { EditServerOption } from './EditServer';
import { ServerLaunchButton } from './ServerLaunchButton';
import { ServerPowerButton } from './ServerPowerButton';
import { ServerStatus } from './ServerStatus';

type ServerControlsProps = {
  server: Editor;
  sx?: SxProps;
  onEdit?: () => void;
  onDelete?: () => void;
};

export const ServerControls = ({ server, sx, onEdit, onDelete }: ServerControlsProps) => {
  const { organizationId } = useOrganizationContext();

  const { showModal: showManageMembersModal } = useManageServerMembersModal({
    componentProps: {
      organizationId,
      editorId: server?.editorId,
      onChange: onEdit,
    },
    modalProps: {},
  });

  return (
    <Group alignItems="center" justifyContent="flex-end" sx={sx}>
      <ServerStatus status={server?.status} startedAt={server?.startedAt} sx={{ mr: 1.5 }} />
      <ServerLaunchButton server={server} />
      <ServerPowerButton server={server} onChange={onEdit} />
      <OptionsMenu
        Icon={SettingsIcon}
        customItems
        size={16}
        menuProps={{
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
          transformOrigin: { vertical: 'top', horizontal: 'right' },
        }}
      >
        {(handleClose) => {
          const items = [
            <CopySshLaunchCommandMenuItem
              key="copy-ssh-launch-command"
              editorId={server?.editorId}
            />,
            <Divider flexItem sx={{ mx: 2 }} key="divider" />,
            <EditServerOption
              key="edit-server"
              server={server}
              onClick={handleClose}
              onEdit={onEdit}
            />,
          ];

          items.push(
            <MenuItem
              key="manage-access"
              onClick={(e) => {
                handleClose(e);
                showManageMembersModal();
              }}
            >
              <Group alignItems="center" gap={2}>
                <UserIcon size={14} />
                <Typography variant="caption2">Share</Typography>
              </Group>
            </MenuItem>,
          );

          items.push(
            <DeleteServerOption
              key="delete-server"
              onClick={handleClose}
              server={server}
              onDelete={onDelete}
            />,
          );

          return items;
        }}
      </OptionsMenu>
    </Group>
  );
};
